import React, { useEffect, useRef, useState } from "react";
import styles from "./Rewards.module.css";
import HeaderBar from "../HeaderBar/HeaderBar";
import {
  getAppSumoFeedback,
  submitAppSumoFeedback,
} from "../../Store/apiStore";
import {
  HowItWorksBadge,
  GiftIcon,
  CheckCircleIcon,
  ClockIcon,
  UploadIcon,
  G2Logo,
  CapterraLogo,
  PlayLogo,
  AppSumoLogo,
  ProductHuntLogo,
} from "./icons";

// Presentation only — logo and where "Go" leads. Which platforms exist, what
// each row is worth, and how far this account has got all come from the server.
const PLATFORM_META = {
  appsumo: {
    Logo: AppSumoLogo,
    text: "Review the deal on AppSumo",
    url: "https://appsumo.com/",
  },
  g2: {
    Logo: G2Logo,
    text: "Share your feedback on G2",
    url: "https://www.g2.com/products/rexpt-ai-virtual-receptionist/reviews",
  },
  play: {
    Logo: PlayLogo,
    text: "Leave a review on Google Play",
    url: "https://play.google.com/store/apps/details?id=com.rexptin&hl=en_SG",
  },
  capterra: {
    Logo: CapterraLogo,
    text: "Review us on Capterra",
    url: "https://www.capterra.in/software/1100205/Rexpt-AI-Receptionist",
  },
  producthunt: {
    Logo: ProductHuntLogo,
    text: "Review us on Product Hunt",
    url: "https://www.producthunt.com/products/rexpt",
  },
};

/**
 * "Give Feedback" — review a platform, upload the screenshot, earn minutes.
 *
 * A row moves through four states, and the control on its right is whichever
 * one the state calls for:
 *
 *   available  Go        opens the review page in a new tab
 *   (opened)   Submit    appears once Go has been pressed; picks the screenshot
 *   pending    Under Review awaiting an admin, nothing to press
 *   approved   +50 mins  in green, the reward landed
 *
 * "Opened" is the one state the server does not know about — it is a local note
 * that this person has been sent to the review page, so Submit does not sit
 * there inviting an upload from someone who has not written a review yet.
 */
// Which review pages this person has already been sent to. Persisted, because
// the journey it tracks leaves the app: tap Go, write the review on another
// site, come back — often much later, or after the app was closed. Held only in
// React state it would be gone by then, and the user would find a Go button
// where their Submit should be, with a screenshot and nowhere to put it.
//
// sessionStorage rather than localStorage: it is a hint about what this person
// is in the middle of, not a fact about their account, and the server already
// holds the facts.
const OPENED_KEY = "appsumoFeedbackOpened";

const readOpened = () => {
  try {
    const raw = sessionStorage.getItem(OPENED_KEY);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch {
    return new Set();
  }
};

const writeOpened = (set) => {
  try {
    sessionStorage.setItem(OPENED_KEY, JSON.stringify([...set]));
  } catch {
    /* storage blocked — Submit then only lasts the session, as before */
  }
};

const GiveFeedback = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [opened, setOpened] = useState(readOpened);
  const [busy, setBusy] = useState(null);
  const [errors, setErrors] = useState({});
  // Which platform's upload dialog is open, if any. Submit opens this rather
  // than the file picker directly: the picker on its own says nothing about
  // WHAT to attach, and a screenshot of the wrong thing is a rejected claim
  // and a round-trip through support.
  const [uploadFor, setUploadFor] = useState(null);
  const fileInputs = useRef({});

  const load = async () => {
    try {
      setData(await getAppSumoFeedback());
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleGo = (key, url) => {
    window.open(url, "_blank", "noopener,noreferrer");
    setOpened((prev) => {
      const next = new Set(prev).add(key);
      writeOpened(next);
      return next;
    });
  };

  const handleFile = async (key, file) => {
    if (!file) return;
    setUploadFor(null);

    // Checked here as well as on the server so the user is told immediately
    // rather than after the upload round-trip.
    if (!file.type.startsWith("image/")) {
      setErrors((e) => ({ ...e, [key]: "Please choose an image file." }));
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setErrors((e) => ({ ...e, [key]: "That image is larger than 8MB." }));
      return;
    }

    setErrors((e) => ({ ...e, [key]: null }));
    setBusy(key);
    try {
      await submitAppSumoFeedback(key, file);
      // Uploaded — the server now owns this platform's state, so the local
      // "they went to review it" hint has done its job.
      setOpened((prev) => {
        const next = new Set(prev);
        next.delete(key);
        writeOpened(next);
        return next;
      });
      await load();
    } catch (err) {
      setErrors((e) => ({
        ...e,
        [key]:
          err?.response?.data?.message ||
          "Could not upload that screenshot. Try again.",
      }));
    } finally {
      setBusy(null);
      // Let the same file be picked again after a failure — without this the
      // input holds it and onChange never fires a second time.
      if (fileInputs.current[key]) fileInputs.current[key].value = "";
    }
  };

  const platforms = data?.platforms || [];
  const bonusMinutes = data?.bonusMinutes || 0;
  const approvedCount = data?.approvedCount || 0;
  const totalPlatforms = data?.totalPlatforms || platforms.length;
  const earnedMinutes = data?.earnedMinutes || 0;
  const completed = !!data?.completed;

  return (
    <div className={styles.wrap}>
      <div className={styles.inner}>
        {/* No strapline under the title: the banner immediately below already
            says what the screen is for. */}
        <div className={styles.headerSlot}>
          <HeaderBar title="Give Feedback" />
        </div>

        <div className={styles.highlight}>
          <div className={styles.highlightCopy}>
            <h2 className={styles.highlightTitle}>
              {completed
                ? `${earnedMinutes} mins earned`
                : `Earn ${bonusMinutes} mins`}
            </h2>
            <p className={styles.highlightText}>
              {completed
                ? "Your feedback bonus has been added to your balance. Thank you."
                : `Complete feedback on any platform and we'll credit minutes to your account`}
            </p>

          </div>
          <span className={styles.highlightGift}>
            <GiftIcon width="100%" height="100%" />
          </span>
        </div>

        <h2 className={styles.waysHeading}>
          <span>Available tasks</span>
        </h2>

        {loading ? (
          <div className={styles.loading}>Loading…</div>
        ) : (
          <div className={styles.taskList}>
            {platforms.map((p) => {
              const meta = PLATFORM_META[p.key];
              if (!meta) return null;
              const { Logo } = meta;
              const isBusy = busy === p.key;
              const canSubmit =
                p.status === "available" || p.status === "rejected";

              return (
                <div className={styles.taskRow} key={p.key}>
                  <span className={styles.taskLogo}>
                    <Logo />
                  </span>

                  <div className={styles.rowMain}>
                    <span className={styles.rowName}>{p.name}</span>
                    <span className={styles.rowSub}>{meta.text}</span>
                    {/* Once approved the line stops being an offer and becomes
                        a receipt: "+75 mins ✓ Verified" rather than "Earn 75
                        mins", which would read as still available. */}
                    {p.status === "approved" ? (
                      <span className={styles.earnedLine}>
                        <span className={styles.earnedAmount}>
                          +{p.minutes} mins
                        </span>
                        <span className={styles.verifiedNote}>
                          <CheckCircleIcon width={13} height={13} />
                          Verified
                        </span>
                      </span>
                    ) : (
                      <span className={styles.wayEarn} >
                        Earn {p.minutes} mins
                      </span>
                    )}
                    {p.status === "rejected" && p.rejectReason && (
                      <span className={styles.rejectNote}>
                        {p.rejectReason} — upload a new screenshot.
                      </span>
                    )}
                    {errors[p.key] && (
                      <span className={styles.uploadError}>{errors[p.key]}</span>
                    )}
                  </div>

                  {p.status === "approved" ? (
                    // The chip states the outcome; the amount and the tick sit
                    // on the line to its left, so this does not repeat them.
                    <span className={styles.earnedTag}>Completed</span>
                  ) : p.status === "pending" ? (
                    <span className={styles.pendingTag}>
                      <ClockIcon width={13} height={13} />
                      Under Review
                    </span>
                  ) : canSubmit && opened.has(p.key) ? (
                    <>
                      <input
                        type="file"
                        accept="image/*"
                        hidden
                        ref={(el) => {
                          fileInputs.current[p.key] = el;
                        }}
                        onChange={(e) => handleFile(p.key, e.target.files?.[0])}
                      />
                      <button
                        type="button"
                        className={styles.submitBtn}
                        disabled={isBusy}
                        onClick={() => setUploadFor(p)}
                      >
                        {isBusy ? "Uploading…" : "Submit"}
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className={styles.goBtn}
                      onClick={() => handleGo(p.key, meta.url)}
                    >
                      Go
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* The four steps, then the timing rule below a rule — the steps say
            what to do, the footer says when the reward lands, and they are
            separated so the second is not read as a fifth step. */}
        <div className={styles.howBox}>
          <h3 className={styles.howTitle}>How it works</h3>
          <ol className={styles.howSteps}>
            <li>Click a platform above</li>
            <li>Leave honest feedback</li>
            <li>Submit proof for verification</li>
            <li>We verify and credit minutes</li>
          </ol>
          <HowItWorksBadge className={styles.howBadge} />
          <div className={styles.howFoot}>
            <span className={styles.howFootTitle}>
              Minutes are credited within 24-48 hrs
            </span>
            <span className={styles.howFootSub}>
              After we verify your feedback
            </span>
          </div>
        </div>
      </div>

      {/* What to attach, said before the file picker opens. A reviewer has to
          be able to see the published review and that it is this user's, so the
          two things worth spelling out are what has to be visible in the shot
          and that an edited one will be turned down. */}
      {uploadFor && (
        <div
          className={styles.modalScrim}
          onClick={() => setUploadFor(null)}
          role="presentation"
        >
          <div
            className={styles.modal}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="upload-modal-title"
          >
            <span className={styles.modalIcon}>
              <UploadIcon />
            </span>

            <h2 className={styles.modalTitle} id="upload-modal-title">
              Upload your {uploadFor.name} review
            </h2>
            <p className={styles.modalText}>
              Take a screenshot of the review you published and upload it here.
              We check it before the minutes are credited.
            </p>

            <ul className={styles.modalList}>
              <li>Your review text, as it appears on {uploadFor.name}</li>
              <li>Your name or profile on the review</li>
              <li>The page must be published — drafts do not count</li>
            </ul>

            <p className={styles.modalNote}>
              Upload the original screenshot. Edited images are rejected.
            </p>

            <button
              type="button"
              className={styles.modalBtn}
              onClick={() => fileInputs.current[uploadFor.key]?.click()}
            >
              Choose screenshot
            </button>
            <button
              type="button"
              className={styles.modalCancel}
              onClick={() => setUploadFor(null)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GiveFeedback;
